/**
 * Shared operating-hours UI for the portal autopilots.
 *  - ScheduleEditor: day chips + start/end times + on/off switch
 *  - ScheduleBadge:  live "Active until 18:00" / "resumes Mon 08:00" pill
 * Times are UK (Europe/London) — matches the backend gate.
 */
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Clock } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function ScheduleEditor({ value, onChange, testidPrefix = "ap-schedule", tzLabel = "UK time" }) {
  const days = value.schedule_days || [];
  const toggleDay = (d) => {
    const next = days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort();
    if (next.length === 0) return; // at least one day
    onChange({ schedule_days: next });
  };
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-slate-500" /> Operating hours ({tzLabel})
          </div>
          <div className="text-[11px] text-slate-500">
            Autopilot only runs inside this window and auto-resumes at the next one.
            Switch off to run 24/7. The master switch above always wins.
          </div>
        </div>
        <Switch
          checked={!!value.schedule_enabled}
          onCheckedChange={(v) => onChange({ schedule_enabled: v })}
          data-testid={`${testidPrefix}-enabled`}
        />
      </div>
      <div className={`flex items-center gap-3 flex-wrap ${value.schedule_enabled ? "" : "opacity-40 pointer-events-none"}`}>
        <div className="flex gap-1">
          {DAYS.map((label, d) => (
            <button
              key={label}
              type="button"
              onClick={() => toggleDay(d)}
              className={`h-7 w-9 rounded text-[10px] font-semibold border transition-colors ${
                days.includes(d)
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-500 border-slate-300 hover:bg-slate-100"
              }`}
              data-testid={`${testidPrefix}-day-${label.toLowerCase()}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
          <Input
            type="time"
            value={value.schedule_start || "08:00"}
            onChange={(e) => e.target.value && onChange({ schedule_start: e.target.value })}
            className="h-7 text-xs w-24"
            data-testid={`${testidPrefix}-start`}
          />
          <span>to</span>
          <Input
            type="time"
            value={value.schedule_end || "18:00"}
            onChange={(e) => e.target.value && onChange({ schedule_end: e.target.value })}
            className="h-7 text-xs w-24"
            data-testid={`${testidPrefix}-end`}
          />
        </div>
      </div>
    </div>
  );
}

export function ScheduleBadge({ schedule, testid = "ap-schedule-badge" }) {
  if (!schedule) return null;
  if (!schedule.schedule_enabled) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600" data-testid={testid}>
        <Clock className="w-3 h-3" /> 24/7
      </span>
    );
  }
  return schedule.within_hours ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700" data-testid={testid}>
      <Clock className="w-3 h-3" /> In hours {schedule.next_change_label}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800" data-testid={testid}>
      <Clock className="w-3 h-3" /> Outside hours — {schedule.next_change_label}
    </span>
  );
}
